import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Image,
  Platform,
  LayoutAnimation,
  UIManager,
  type TextStyle,
  type ViewStyle,
} from 'react-native';
import { Clickable, PRESS_PRESETS } from '../../components/common/Clickable';
import { SvgIcon } from '../../components/common/SvgIcon';
import { appTheme, useThemeColors, type ThemeColors } from '../../shared/theme';
import { hexToRgba } from '../../shared/theme/colorUtils';
import { androidScaleFont, androidScaleSize } from '../../shared/theme/androidUiParity';
import { horizontalLtrDirectionStyle } from '../../shared/i18n/rtl';
import { useRtl } from '../../shared/i18n/useRtl';
import { DASH_STUDIO_INNER, DASH_STUDIO_RADIUS, studioCardShell } from '../../shared/theme/studio/dashboardConstants';
import { studioSectionLabelStyle } from '../../shared/theme/studio/stackScreenShell';

if (Platform.OS === 'android' && UIManager.setLayoutAnimationEnabledExperimental) {
  UIManager.setLayoutAnimationEnabledExperimental(true);
}

const ANDROID_TEXT_COMPACT: TextStyle = Platform.OS === 'android' ? { includeFontPadding: false } : {};
const ICON_SQUIRCLE = androidScaleSize(44);
const PLAN_THUMB_WIDTH = androidScaleSize(88);

/** Dashboard-parity card shell — keep overflow visible so iOS shadows render. */
function studio_card_shell(colors: ThemeColors): ViewStyle {
  return {
    ...studioCardShell(colors),
    backgroundColor: colors.surface,
    borderRadius: DASH_STUDIO_RADIUS,
    padding: 0,
    width: '100%',
  };
}

export const CheckoutSectionBlock = memo(function CheckoutSectionBlock({
  label,
  children,
  style,
}: {
  label: string;
  children: React.ReactNode;
  style?: ViewStyle;
}) {
  const colors = useThemeColors() ?? appTheme.colors;
  const label_style = useMemo(
    () => StyleSheet.flatten([studioSectionLabelStyle(colors), { marginStart: 0, marginBottom: appTheme.spacing.sm }]),
    [colors],
  );

  return (
    <View style={[styles.section_block, style]}>
      <Text style={label_style}>{label}</Text>
      {children}
    </View>
  );
});

export const CheckoutStudioCard = memo(function CheckoutStudioCard({
  children,
  style,
}: {
  children: React.ReactNode;
  style?: ViewStyle;
}) {
  const colors = useThemeColors() ?? appTheme.colors;
  const card_style = useMemo(() => studio_card_shell(colors), [colors]);

  return <View style={[card_style, style]}>{children}</View>;
});

/**
 * Collapsible studio card: compact header when closed, body expands on tap.
 * Use for sparse optional sections (coupon, add-ons) so the form does not look empty.
 */
export const CheckoutCollapsibleSection = memo(function CheckoutCollapsibleSection({
  label,
  collapsed_summary,
  expanded,
  on_toggle,
  children,
  style,
}: {
  label: string;
  collapsed_summary: string;
  expanded: boolean;
  on_toggle: () => void;
  children: React.ReactNode;
  style?: ViewStyle;
}) {
  const colors = useThemeColors() ?? appTheme.colors;
  const rtl = useRtl();

  return (
    <View style={[styles.section_block, style]}>
      <CheckoutStudioCard>
        <Clickable
          onPress={on_toggle}
          pressScale={PRESS_PRESETS.listRow.scale}
          haptics
          accessibilityRole="button"
          accessibilityState={{ expanded }}
          accessibilityLabel={label}
          style={styles.collapse_header}>
          <View style={[styles.collapse_header_row, rtl.flipRowStyle]}>
            <Text style={[styles.collapse_label, { color: colors.text, textAlign: rtl.textAlign }]} numberOfLines={1}>
              {label}
            </Text>
            <View style={[styles.collapse_trailing, rtl.flipRowStyle]}>
              {!expanded ? (
                <Text
                  style={[styles.collapse_summary, { color: colors.textSecondary, textAlign: rtl.textAlign }]}
                  numberOfLines={1}>
                  {collapsed_summary}
                </Text>
              ) : null}
              <View style={expanded ? styles.chevron_expanded : undefined}>
                <SvgIcon name="chevron-down" size={18} color={colors.textLight} />
              </View>
            </View>
          </View>
        </Clickable>
        {expanded ? (
          <>
            <CheckoutInCardDivider />
            {children}
          </>
        ) : null}
      </CheckoutStudioCard>
    </View>
  );
});

export function useCheckoutCollapse(initial: boolean, force_open?: boolean) {
  const [expanded, set_expanded] = useState(initial || Boolean(force_open));

  useEffect(() => {
    if (force_open) {
      set_expanded(true);
    }
  }, [force_open]);

  const toggle = useCallback(() => {
    LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
    set_expanded(prev => !prev);
  }, []);

  return { expanded, set_expanded, toggle };
}

export const CheckoutInCardDivider = memo(function CheckoutInCardDivider() {
  const colors = useThemeColors() ?? appTheme.colors;
  return <View style={[styles.divider, { backgroundColor: colors.borderLight ?? colors.border }]} />;
});

export const CheckoutNavRow = memo(function CheckoutNavRow({
  icon,
  label,
  value,
  hint,
  on_press,
  disabled,
}: {
  icon: string;
  label: string;
  value: string;
  hint?: string;
  on_press?: () => void;
  disabled?: boolean;
}) {
  const colors = useThemeColors() ?? appTheme.colors;
  const rtl = useRtl();
  const chevron = rtl.trailingRowDisclosureChevron;
  const icon_bg = (hexToRgba(colors.primary, 0.1) as string) || colors.primaryUltra;

  const inner = (
    <View style={styles.nav_row}>
      <View style={[styles.icon_squircle, { backgroundColor: icon_bg }]}>
        <SvgIcon name={icon as any} size={22} color={colors.primary} />
      </View>
      <View style={styles.nav_text_col}>
        <Text style={[styles.nav_label, { color: colors.textSecondary, textAlign: rtl.textAlign }]} numberOfLines={1}>
          {label}
        </Text>
        <Text style={[styles.nav_value, { color: colors.text, textAlign: rtl.textAlign }]} numberOfLines={2}>
          {value}
        </Text>
        {hint ? (
          <Text style={[styles.nav_hint, { color: colors.textLight, textAlign: rtl.textAlign }]} numberOfLines={2}>
            {hint}
          </Text>
        ) : null}
      </View>
      {on_press ? (
        <View style={styles.chevron_col}>
          <SvgIcon name={chevron} size={20} color={colors.textLight} />
        </View>
      ) : null}
    </View>
  );

  if (!on_press || disabled) {
    return <View style={styles.nav_row_wrap}>{inner}</View>;
  }

  return (
    <Clickable
      onPress={on_press}
      disabled={disabled}
      pressScale={PRESS_PRESETS.listRow.scale}
      haptics
      style={styles.nav_row_wrap}
      accessibilityRole="button">
      {inner}
    </Clickable>
  );
});

export const CheckoutPlanHeroCard = memo(function CheckoutPlanHeroCard({
  eyebrow,
  title,
  image_uri,
  meals_label,
  snacks_label,
  days_label,
  price_label,
  price_value,
  meal_config_rows,
}: {
  eyebrow: string;
  title: string;
  image_uri: string | null;
  meals_label: string;
  snacks_label: string;
  days_label: string;
  price_label: string;
  price_value: string;
  /** Custom plans: one row per meal with protein/carbs summary. */
  meal_config_rows?: Array<{ label: string; value: string }>;
}) {
  const colors = useThemeColors() ?? appTheme.colors;
  const rtl = useRtl();
  const [image_failed, set_image_failed] = useState(false);
  const card_style = useMemo(() => studio_card_shell(colors), [colors]);
  const primary_tint = (hexToRgba(colors.primary, 0.14) as string) || colors.primaryFade;
  const chip_neutral = (hexToRgba(colors.text, 0.05) as string) || colors.backgroundFade;
  const has_meal_config = Boolean(meal_config_rows && meal_config_rows.length > 0);

  return (
    <View style={card_style}>
      <View style={[styles.plan_row, horizontalLtrDirectionStyle]}>
        <View style={[styles.plan_thumb, { backgroundColor: colors.backgroundFade ?? chip_neutral }]}>
          {image_uri && !image_failed ? (
            <Image
              source={{ uri: image_uri }}
              style={StyleSheet.absoluteFillObject}
              resizeMode="cover"
              onError={() => set_image_failed(true)}
            />
          ) : (
            <View
              style={[
                StyleSheet.absoluteFillObject,
                { backgroundColor: primary_tint, alignItems: 'center', justifyContent: 'center' },
              ]}>
              <SvgIcon name="menu" size={28} color={colors.primary} />
            </View>
          )}
        </View>
        <View style={styles.plan_text_col}>
          <Text style={[styles.plan_eyebrow, { color: colors.textLight, textAlign: rtl.textAlign }]} numberOfLines={1}>
            {eyebrow}
          </Text>
          <Text style={[styles.plan_title, { color: colors.text, textAlign: rtl.textAlign }]} numberOfLines={2}>
            {title}
          </Text>
          <View style={[styles.chip_row, rtl.flipRowStyle]}>
            <View style={[styles.chip, { backgroundColor: primary_tint }]}>
              <Text style={[styles.chip_text, { color: colors.text }]} numberOfLines={1}>
                {meals_label}
              </Text>
            </View>
            <View style={[styles.chip, { backgroundColor: chip_neutral }]}>
              <Text style={[styles.chip_text, { color: colors.textSecondary }]} numberOfLines={1}>
                {snacks_label}
              </Text>
            </View>
            <View style={[styles.chip, { backgroundColor: chip_neutral }]}>
              <Text style={[styles.chip_text, { color: colors.textSecondary }]} numberOfLines={1}>
                {days_label}
              </Text>
            </View>
          </View>
        </View>
      </View>
      {has_meal_config ? (
        <View style={[styles.plan_meal_config, { borderTopColor: colors.borderLight ?? colors.border }]}>
          {meal_config_rows!.map((row, index) => (
            <View
              key={`${row.label}-${index}`}
              style={[
                styles.plan_meal_config_row,
                rtl.flipRowStyle,
                index < meal_config_rows!.length - 1 && {
                  borderBottomWidth: StyleSheet.hairlineWidth,
                  borderBottomColor: colors.borderLight ?? colors.border,
                },
              ]}>
              <Text style={[styles.plan_meal_config_label, { color: colors.textSecondary, textAlign: rtl.textAlign }]} numberOfLines={1}>
                {row.label}
              </Text>
              <Text style={[styles.plan_meal_config_value, { color: colors.text, textAlign: rtl.textAlign }]} numberOfLines={1}>
                {row.value}
              </Text>
            </View>
          ))}
        </View>
      ) : null}
      <View style={[styles.plan_price_row, { borderTopColor: colors.borderLight ?? colors.border }]}>
        <Text style={[styles.plan_price_label, { color: colors.textSecondary }]}>{price_label}</Text>
        <Text style={[styles.plan_price_value, { color: colors.primary }]}>{price_value}</Text>
      </View>
    </View>
  );
});

export const CheckoutSummaryRow = memo(function CheckoutSummaryRow({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent?: 'discount' | 'total';
}) {
  const colors = useThemeColors() ?? appTheme.colors;
  const value_color =
    accent === 'discount' ? colors.success : accent === 'total' ? colors.primary : colors.text;
  const label_style = accent === 'total' ? styles.summary_total_label : styles.summary_label;
  const value_style = accent === 'total' ? styles.summary_total_value : styles.summary_value;

  return (
    <View style={styles.summary_row}>
      <Text style={[label_style, { color: accent === 'total' ? colors.text : colors.textSecondary }]}>{label}</Text>
      <Text style={[value_style, { color: value_color }]}>{value}</Text>
    </View>
  );
});

const styles = StyleSheet.create({
  section_block: {
    marginTop: appTheme.spacing.md,
  },
  divider: {
    height: StyleSheet.hairlineWidth,
    marginHorizontal: DASH_STUDIO_INNER,
  },
  collapse_header: {
    paddingHorizontal: DASH_STUDIO_INNER,
    paddingVertical: androidScaleSize(14),
  },
  collapse_header_row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: appTheme.spacing.sm,
    minWidth: 0,
  },
  collapse_label: {
    flexShrink: 1,
    fontSize: androidScaleFont(15),
    fontWeight: '800',
    letterSpacing: -0.2,
    ...ANDROID_TEXT_COMPACT,
  },
  collapse_trailing: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flexShrink: 1,
    minWidth: 0,
  },
  collapse_summary: {
    flexShrink: 1,
    fontSize: androidScaleFont(13),
    fontWeight: '600',
    ...ANDROID_TEXT_COMPACT,
  },
  chevron_expanded: {
    transform: [{ rotate: '180deg' }],
  },
  nav_row_wrap: {
    paddingHorizontal: DASH_STUDIO_INNER,
    paddingVertical: appTheme.spacing.md,
  },
  nav_row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: appTheme.spacing.md,
    minWidth: 0,
  },
  icon_squircle: {
    width: ICON_SQUIRCLE,
    height: ICON_SQUIRCLE,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  nav_text_col: {
    flex: 1,
    minWidth: 0,
    gap: 2,
  },
  nav_label: {
    fontSize: androidScaleFont(11),
    fontWeight: '700',
    letterSpacing: 0.4,
    textTransform: 'uppercase',
    ...ANDROID_TEXT_COMPACT,
  },
  nav_value: {
    fontSize: androidScaleFont(16),
    fontWeight: '700',
    lineHeight: androidScaleFont(21),
    ...ANDROID_TEXT_COMPACT,
  },
  nav_hint: {
    fontSize: androidScaleFont(12),
    fontWeight: '500',
    lineHeight: androidScaleFont(16),
    marginTop: 2,
    ...ANDROID_TEXT_COMPACT,
  },
  chevron_col: {
    justifyContent: 'center',
    minWidth: 20,
  },
  plan_row: {
    flexDirection: 'row',
    alignItems: 'stretch',
    gap: appTheme.spacing.md,
    padding: DASH_STUDIO_INNER,
    minWidth: 0,
  },
  plan_thumb: {
    width: PLAN_THUMB_WIDTH,
    minHeight: PLAN_THUMB_WIDTH,
    borderRadius: 12,
    overflow: 'hidden',
    alignSelf: 'stretch',
  },
  plan_text_col: {
    flex: 1,
    minWidth: 0,
    justifyContent: 'center',
    gap: 2,
  },
  plan_eyebrow: {
    fontSize: androidScaleFont(10),
    fontWeight: '800',
    letterSpacing: 0.7,
    textTransform: 'uppercase',
    ...ANDROID_TEXT_COMPACT,
  },
  plan_title: {
    fontSize: androidScaleFont(17),
    fontWeight: '800',
    lineHeight: androidScaleFont(22),
    ...ANDROID_TEXT_COMPACT,
  },
  chip_row: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
    marginTop: 8,
    alignItems: 'center',
  },
  chip: {
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 10,
  },
  chip_text: {
    fontSize: androidScaleFont(11),
    fontWeight: '700',
    letterSpacing: 0.2,
    ...ANDROID_TEXT_COMPACT,
  },
  plan_price_row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: DASH_STUDIO_INNER,
    paddingVertical: appTheme.spacing.md,
    borderTopWidth: StyleSheet.hairlineWidth,
    gap: appTheme.spacing.sm,
  },
  plan_meal_config: {
    borderTopWidth: StyleSheet.hairlineWidth,
    paddingHorizontal: DASH_STUDIO_INNER,
    paddingVertical: appTheme.spacing.xs,
  },
  plan_meal_config_row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: appTheme.spacing.sm,
    paddingVertical: appTheme.spacing.sm,
    minWidth: 0,
  },
  plan_meal_config_label: {
    flexShrink: 0,
    fontSize: androidScaleFont(13),
    fontWeight: '600',
    ...ANDROID_TEXT_COMPACT,
  },
  plan_meal_config_value: {
    flex: 1,
    minWidth: 0,
    fontSize: androidScaleFont(13),
    fontWeight: '700',
    fontVariant: ['tabular-nums'],
    ...ANDROID_TEXT_COMPACT,
  },
  plan_price_label: {
    fontSize: androidScaleFont(13),
    fontWeight: '600',
    flex: 1,
    ...ANDROID_TEXT_COMPACT,
  },
  plan_price_value: {
    fontSize: androidScaleFont(20),
    fontWeight: '800',
    letterSpacing: -0.3,
    ...ANDROID_TEXT_COMPACT,
  },
  summary_row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: appTheme.spacing.sm,
    gap: appTheme.spacing.md,
  },
  summary_label: {
    fontSize: androidScaleFont(14),
    fontWeight: '500',
    flex: 1,
    ...ANDROID_TEXT_COMPACT,
  },
  summary_value: {
    fontSize: androidScaleFont(15),
    fontWeight: '700',
    textAlign: 'right',
    ...ANDROID_TEXT_COMPACT,
  },
  summary_total_label: {
    fontSize: androidScaleFont(16),
    fontWeight: '800',
    flex: 1,
    ...ANDROID_TEXT_COMPACT,
  },
  summary_total_value: {
    fontSize: androidScaleFont(22),
    fontWeight: '800',
    letterSpacing: -0.4,
    textAlign: 'right',
    ...ANDROID_TEXT_COMPACT,
  },
});
